<template>
  <SelectionTable
    :columns="tableColumns"
    :items="sortedCollectionIds"
    :scroll-to-item="preselectedCollectionId"
    @touch-swipe="handleTouchSwipe"
    class="pt-3.75"
  >
    <template #cell-0="{ item }">
      <div
        :class="[
          'rounded-l-[calc(var(--border-radius)*1px)] rounded-r-[3px] text-text bg-main-background flex justify-center items-center relative w-full h-full p-2 text-balance text-center',
          $typo.openSans12,
        ]"
      >
        {{ collectionLabelMap[item] }}
      </div>
    </template>
    <template #cell-1="{ item }">
      <div
        :class="[
          'rounded-[3px] text-text bg-main-background flex h-full p-2 justify-center items-center relative w-full',
          $typo.openSans12,
        ]"
      >
        {{ collectionVolumeRangeMap[item] }}
      </div>
    </template>
    <template #cell-2="{ item, setRef }">
      <div
        :class="[
          'rounded-r-[calc(var(--border-radius)*1px)] rounded-l-[3px] text-text flex h-full justify-center items-center relative w-full gap-1',
          $typo.openSans12,
          getCollectionQuantity(item) > 0 ? 'bg-(--atsume-main)' : 'bg-button',
          {
            'opacity-50': notReleasedMap[item],
          },
        ]"
        :data-active="getCollectionQuantity(item) > 0"
        :ref="(el: any) => setRef(el)"
        :data-item="item"
      >
        <IconCustom
          v-if="preselectedIcon && preselectedCollectionId === item"
          :ligature="preselectedIcon"
          size="16"
        />
        {{ t('releaseSelectionTableOwnedCell') }}
      </div>
    </template>
  </SelectionTable>
</template>

<script lang="ts" setup>
import dayjs from 'dayjs'
import { computed, onMounted, toRefs } from 'vue'
import SelectionTable, { type ColumnConfig } from './SelectionTable.vue'
import { useLocalizationStore } from '@/stores/localization.ts'
import $typo from '@/styles/typografy.module.css'
import { MiscOwnedMap } from '@/stores/user-collection'
import {
  ReleaseItemCollection,
  UserMiscPossessionActivityTypeType,
} from '../../__generated__/graphql.ts'
import { formatVolumeNumber } from '@/utils'
import { groupCollectionVolumeRangesByEntry } from '@/utils/collection'
import type { IconName } from '@/icons'
import IconCustom from './IconCustom.vue'

interface Props {
  collectionEntries: ReleaseItemCollection[]
  miscOwnedMap: MiscOwnedMap
  entryId: string
  preselectedCollectionId?: string
  preselectedIcon?: IconName
}

const props = defineProps<Props>()

const { collectionEntries, miscOwnedMap, entryId, preselectedCollectionId, preselectedIcon } =
  toRefs(props)

const collectionSpansMultipleEntries = (collection: ReleaseItemCollection) => {
  const entryIds = new Set(
    (collection.releaseItems ?? [])
      .map((releaseItem) => releaseItem.entryId)
      .filter((entryId): entryId is string => Boolean(entryId)),
  )
  return entryIds.size > 1
}

const collectionLabelMap = computed(() =>
  Object.fromEntries(
    collectionEntries.value.map((c) => {
      if (collectionSpansMultipleEntries(c) && c.localizedSeriesTitle) {
        return [c.id, c.localizedSeriesTitle]
      }
      const prefix = c.name === 'bundle' ? t('collectionPrefix') : t('slipCasePrefix')
      const number = c.number ? formatVolumeNumber(c.number) : ''
      return [c.id, number ? `${prefix} ${number}` : prefix]
    }),
  ),
)

const collectionVolumeRangeMap = computed(() =>
  Object.fromEntries(
    collectionEntries.value.map((c) => {
      if (collectionSpansMultipleEntries(c)) {
        const volumeCount = c.releaseItems?.length ?? 0
        if (volumeCount === 0) return [c.id, t('emptySlipcase')]
        return [c.id, t('releaseEntryVolumeAmountLine', { count: volumeCount })]
      }
      const segments = groupCollectionVolumeRangesByEntry(c.releaseItems ?? [], entryId.value)
      const parts = [
        ...(segments.ownRange ? [t('collectionRangeLabel', { range: segments.ownRange })] : []),
        ...segments.foreign.map((foreign) =>
          t('collectionRangeLabelWithTitle', { title: foreign.title, range: foreign.range }),
        ),
      ]
      if (parts.length === 0) return [c.id, t('emptySlipcase')]
      return [c.id, parts.join(' + ')]
    }),
  ),
)

const notReleasedMap = computed(() => {
  const today = dayjs()
  return Object.fromEntries(
    collectionEntries.value
      .filter((c) => !c.releaseDate || dayjs(c.releaseDate).isAfter(today, 'day'))
      .map((c) => [c.id, true]),
  )
})

const sortedCollectionIds = computed(() => {
  const collectionsWithItemCount = collectionEntries.value.map((c) => ({
    id: c.id,
    nameIndex: c.name === 'bundle' ? 0 : 1,
    number: Number(c.number),
    itemCount: c.releaseItems?.length ?? 0,
  }))

  return collectionsWithItemCount
    .sort((a, b) => {
      if (a.nameIndex !== b.nameIndex) {
        return a.nameIndex - b.nameIndex
      }
      if (a.number !== b.number) {
        return a.number - b.number
      }
      return a.itemCount - b.itemCount
    })
    .map((c) => c.id)
})

const emit = defineEmits<{
  (e: 'update:miscOwnedMap', value: MiscOwnedMap): void
  (e: 'miscOwnedMapUpdate', id: string, value: MiscOwnedMap[string]): void
}>()

const { t } = useLocalizationStore()

const collectionGroupKey = (id: string) => collectionLabelMap.value[id] ?? id

const tableColumns = computed<ColumnConfig<string>[]>(() => [
  {
    groupBy: collectionGroupKey,
  },
  {},
  {
    onCellClick: toggleOwned,
  },
])

function getCollectionQuantity(id: string) {
  return miscOwnedMap.value[id]?.quantity || 0
}

onMounted(() => {
  if (preselectedCollectionId.value) {
    const newOwnedMap = { ...miscOwnedMap.value }
    if (!newOwnedMap[preselectedCollectionId.value]) {
      newOwnedMap[preselectedCollectionId.value] = {
        type: UserMiscPossessionActivityTypeType.Collection,
        quantity: 1,
      }
      emit('update:miscOwnedMap', newOwnedMap)
    }
  }
})

function toggleOwned(id: string) {
  const newOwnedMap = { ...miscOwnedMap.value }
  if (!newOwnedMap[id]) {
    newOwnedMap[id] = {
      type: UserMiscPossessionActivityTypeType.Collection,
      quantity: 0,
    }
  }
  newOwnedMap[id] = {
    type: UserMiscPossessionActivityTypeType.Collection,
    quantity: newOwnedMap[id].quantity > 0 ? 0 : 1,
  }
  emit('miscOwnedMapUpdate', id, newOwnedMap[id])
  emit('update:miscOwnedMap', newOwnedMap)
}

function handleTouchSwipe({
  columnIndex,
  item,
  swipeMode,
}: {
  columnIndex: number
  item: string
  swipeMode: boolean
}) {
  if (columnIndex !== 2) return
  const newOwnedMap = {
    ...miscOwnedMap.value,
    [item]: {
      type: UserMiscPossessionActivityTypeType.Collection,
      quantity: swipeMode ? 1 : 0,
    },
  }
  emit('update:miscOwnedMap', newOwnedMap)
}
</script>
